import React, { useEffect, useRef } from 'react';
import css from './TermsAndConditions.module.scss';
import { useKioskState, useNoAnimations } from '../../../context/Kiosk/kiosk';
import { useLocale, useLocalization } from '../../../context/Localization/localization';
import { ImageHelper } from '../../../controls/ImageHelper/ImageHelper';
import { TransformWrapper, TransformComponent, ReactZoomPanPinchRef } from 'react-zoom-pan-pinch';
import { PdfHelper } from '../../../controls/PdfHelper/PdfHelper';
import { GMSizedIcon } from '../../../controls/Icon';
import { GMCheckbox } from '../../../controls/Checkbox';

interface IProps {
    termsAcknowledged: boolean;
    setTermsAcknowledged: (val: boolean) => void;
    setPopUpIsOpen?: (val: boolean) => void;
}

export const TermsAndConditions: React.FC<IProps> = ({ termsAcknowledged, setTermsAcknowledged, setPopUpIsOpen }) => {
    const {
        dealer: { primaryLanguage, roBackerEnabled, roBackerAcknowledgmentRequired, roBackerImage, roBackerImageAltLang },
    } = useKioskState();
    const Localization = useLocalization();
    const [openPopUp, setOpenPopUp] = React.useState(false);
    const [pdfHeight, setPdfHeight] = React.useState(0);

    const noAnimations = useNoAnimations();
    const [zoomInClicked, setZoomInClicked] = React.useState(false);
    const [zoomOutClicked, setZoomOutClicked] = React.useState(false);

    const url = useLocale() === primaryLanguage ? roBackerImage : roBackerImageAltLang;
    const isPdf = url?.endsWith('.pdf');
    const fullscreenPdfSizeRef = useRef<HTMLDivElement>(null);
    const zoomRef = React.useRef<ReactZoomPanPinchRef>(null);

    useEffect(() => {
        if (fullscreenPdfSizeRef.current) {
            const resizeObserver = new ResizeObserver(() => {
                const yPos = (fullscreenPdfSizeRef.current?.getBoundingClientRect()?.height ?? 0) / 2 - window.innerHeight / 2 + 30;
                if (yPos > 0) {
                    zoomRef.current?.setTransform(0, yPos, 1.0);
                }
            });
            resizeObserver.observe(fullscreenPdfSizeRef.current);

            return () => resizeObserver.disconnect();
        }

        return;
    }, [openPopUp]);

    return !roBackerEnabled ? null : (
        <div className={css.disclaimerDiv}>
            {openPopUp && (
                <div className={css.popUpContainer}>
                    <div className={css.popUp}>
                        <div className={css.popUpHeader}>{Localization.TermsAndConditionsCapitalized}</div>
                        <div className={css.imageContainer}>
                            <TransformWrapper
                                centerOnInit={!isPdf}
                                ref={zoomRef}
                                panning={
                                    noAnimations
                                        ? {
                                            velocityDisabled: true,
                                        }
                                        : undefined
                                }
                                zoomAnimation={
                                    noAnimations
                                        ? {
                                            disabled: true,
                                            animationTime: 0,
                                        }
                                        : undefined
                                }
                                doubleClick={
                                    noAnimations
                                        ? {
                                            disabled: true,
                                            animationTime: 0,
                                        }
                                        : undefined
                                }
                                disablePadding={true}
                            >
                                {({ zoomIn, zoomOut, resetTransform, ...rest }) => (
                                    <>
                                        <div className={css.tools}>
                                            <div className={css.topTools}>
                                                <button
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                        setOpenPopUp(false);
                                                        setPopUpIsOpen?.(false);
                                                    }}
                                                    title={Localization.ClickToCloseWindow}
                                                >
                                                    <GMSizedIcon
                                                        iconType={'X'}
                                                        ariaLabel={''}
                                                        iconSize={'custom'}
                                                    />
                                                </button>
                                            </div>
                                            <div className={css.bottomTools}>
                                                <button
                                                    className={zoomOutClicked ? css.clickedAni : undefined}
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                        zoomOut(undefined, noAnimations ? 0 : 300);
                                                        if (!noAnimations) {
                                                            setZoomOutClicked(true);
                                                            setTimeout(function () {
                                                                setZoomOutClicked(false);
                                                            }, 250);
                                                        }
                                                    }}
                                                    title={Localization.ClickToZoomOut}
                                                >
                                                    <GMSizedIcon
                                                        iconType={'Dash'}
                                                        ariaLabel={''}
                                                        iconSize={'custom'}
                                                    />
                                                </button>
                                                <button
                                                    className={zoomInClicked ? css.clickedAni : undefined}
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                        zoomIn(undefined, noAnimations ? 0 : 300);
                                                        if (!noAnimations) {
                                                            setZoomInClicked(true);
                                                            setTimeout(function () {
                                                                setZoomInClicked(false);
                                                            }, 250);
                                                        }
                                                    }}
                                                    title={Localization.ClickToZoomIn}
                                                >
                                                    <GMSizedIcon
                                                        iconType={'Plus'}
                                                        ariaLabel={''}
                                                        iconSize={'custom'}
                                                    />
                                                </button>
                                            </div>
                                        </div>
                                        <TransformComponent
                                            wrapperStyle={{ width: '100vw',
                                                height: 'calc(' + window.innerHeight + 'px - 30px - 2vmin)' }}
                                            contentStyle={isPdf ? { width: '100vw',
                                                height: pdfHeight + 'px' } : undefined}
                                        >
                                            {openPopUp && isPdf ? (
                                                <div ref={fullscreenPdfSizeRef} style={{ height: '1px' }}>
                                                    <PdfHelper
                                                        src={url}
                                                        pageWidthPx={window.innerWidth}
                                                        pdfHeightChanged={(height: number) => {
                                                            setPdfHeight(height);
                                                        }}
                                                    />
                                                </div>
                                            ) : (
                                                <ImageHelper
                                                    imageProps={{
                                                        src: url,
                                                        alt: Localization.TermsAndConditions,
                                                    }}
                                                    fullyQualified
                                                />
                                            )}
                                        </TransformComponent>
                                    </>
                                )}
                            </TransformWrapper>
                        </div>
                    </div>
                </div>
            )}
            {roBackerAcknowledgmentRequired ? (
                <>
                    <div className={css.termsAcknowledgedButton}>
                        <GMCheckbox
                            iconSize={'small'}
                            checked={termsAcknowledged}
                            onChange={() => setTermsAcknowledged(!termsAcknowledged)}
                        />
                    </div>
                    <div>
                        {Localization.formatString(
                            Localization.IHaveReadAndAgreeTo,
                            <div
                                className={css.termsLink}
                                onClick={() => {
                                    setOpenPopUp(true);
                                    setPopUpIsOpen?.(true);
                                }}
                                title={Localization.ClickToDisplayTermsAndConditionsFullscreen}
                            >
                                {Localization.TermsAndConditions}
                            </div>,
                        )}
                    </div>
                </>
            ) : (
                <div>
                    {Localization.formatString(
                        Localization.ReviewOur,
                        <div
                            className={css.termsLink}
                            onClick={() => {
                                setOpenPopUp(true);
                                setPopUpIsOpen?.(true);
                            }}
                            title={Localization.ClickToDisplayTermsAndConditionsFullscreen}
                        >
                            {Localization.TermsAndConditions}
                        </div>,
                    )}
                </div>
            )}
        </div>
    );
};
